import React, { useState, useRef } from 'react';
import {
  HStack,
  IconButton,
  Divider,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Tooltip,
  useColorModeValue,
  Box,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Input,
  Button,
  VStack,
  Text,
} from '@chakra-ui/react';
import {
  FiBold,
  FiItalic,
  FiUnderline,
  FiList,
  FiAlignLeft,
  FiAlignCenter,
  FiAlignRight,
  FiLink,
  FiImage,
  FiChevronDown,
  FiCode,
  FiMinus,
} from 'react-icons/fi';
import {
  MdFormatStrikethrough,
  MdFormatListNumbered,
  MdFormatQuote,
  MdUndo,
  MdRedo,
  MdFormatColorText,
  MdHighlight,
} from 'react-icons/md';

function ToolbarButton({ icon, label, isActive, onClick, isDisabled }) {
  const activeBg = useColorModeValue('blue.50', 'blue.900');
  const activeColor = useColorModeValue('blue.600', 'blue.200');
  const hoverBg = useColorModeValue('gray.100', 'gray.700');

  return (
    <Tooltip label={label} placement="top" hasArrow openDelay={400}>
      <IconButton
        icon={icon}
        aria-label={label}
        size="xs"
        variant={isActive ? 'solid' : 'ghost'}
        colorScheme={isActive ? 'blue' : 'gray'}
        bg={isActive ? activeBg : undefined}
        color={isActive ? activeColor : undefined}
        onClick={onClick}
        isDisabled={isDisabled}
        _hover={{ bg: isActive ? activeBg : hoverBg }}
      />
    </Tooltip>
  );
}

export default function EmailEditorToolbar({ editor, extraButtons }) {
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const bg = useColorModeValue('gray.50', 'gray.800');
  const popoverBg = useColorModeValue('white', 'gray.700');

  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [hasContext, setHasContext] = useState(false);
  const urlInputRef = useRef(null);

  if (!editor) return null;

  // Alignment is context-aware: when an image node is selected, alignment
  // updates the image's `align` attribute; otherwise it sets text alignment.
  const onImage = editor.isActive('image');
  const applyAlign = (dir) => {
    if (onImage) {
      editor.chain().focus().updateAttributes('image', { align: dir }).run();
    } else {
      editor.chain().focus().setTextAlign(dir).run();
    }
  };
  const alignActive = (dir) =>
    onImage ? editor.isActive('image', { align: dir }) : editor.isActive({ textAlign: dir });

  const handleLinkPopoverOpen = () => {
    const { from, to, empty } = editor.state.selection;
    const existingUrl = editor.getAttributes('link').href || '';
    const selectedText = empty ? '' : editor.state.doc.textBetween(from, to, ' ');
    const contextual = !empty || !!existingUrl;

    setLinkUrl(existingUrl || 'https://');
    setLinkText(selectedText);
    setHasContext(contextual);
  };

  const applyLink = (closeFn) => {
    const url = linkUrl.trim();
    if (!url || url === 'https://') {
      if (editor.isActive('link')) {
        editor.chain().focus().extendMarkRange('link').unsetLink().run();
      } else {
        editor.chain().focus().run();
      }
    } else if (hasContext) {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    } else {
      const displayText = linkText.trim() || url;
      editor.chain().focus()
        .insertContent({
          type: 'text',
          text: displayText,
          marks: [{ type: 'link', attrs: { href: url, target: '_blank', rel: 'noopener noreferrer' } }],
        })
        .run();
    }
    closeFn();
  };

  const removeLink = (closeFn) => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    closeFn();
  };

  return (
    <Box
      borderWidth="1px"
      borderBottomWidth="0"
      borderColor={borderColor}
      borderTopRadius="md"
      bg={bg}
      px={2}
      py={1}
      overflowX="auto"
    >
      <HStack spacing={0} flexWrap="wrap">
        {/* Text formatting */}
        <ToolbarButton
          icon={<FiBold />}
          label="Bold (Ctrl+B)"
          isActive={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
        />
        <ToolbarButton
          icon={<FiItalic />}
          label="Italic (Ctrl+I)"
          isActive={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        />
        <ToolbarButton
          icon={<FiUnderline />}
          label="Underline (Ctrl+U)"
          isActive={editor.isActive('underline')}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        />
        <ToolbarButton
          icon={<MdFormatStrikethrough />}
          label="Strikethrough"
          isActive={editor.isActive('strike')}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        />

        <Divider orientation="vertical" h="20px" mx={1} />

        {/* Headings dropdown */}
        <Menu>
          <Tooltip label="Heading" placement="top" hasArrow openDelay={400}>
            <MenuButton
              as={IconButton}
              icon={<FiChevronDown />}
              size="xs"
              variant="ghost"
              aria-label="Heading level"
            >
              {editor.isActive('heading') ? `H${[1,2,3].find(l => editor.isActive('heading', { level: l })) || ''}` : 'H'}
            </MenuButton>
          </Tooltip>
          <MenuList minW="140px" zIndex={10}>
            <MenuItem
              fontSize="sm"
              fontWeight={editor.isActive('paragraph') ? 'bold' : 'normal'}
              onClick={() => editor.chain().focus().setParagraph().run()}
            >
              Normal text
            </MenuItem>
            {[1, 2, 3].map(level => (
              <MenuItem
                key={level}
                fontSize={level === 1 ? 'lg' : level === 2 ? 'md' : 'sm'}
                fontWeight={editor.isActive('heading', { level }) ? 'bold' : 'semibold'}
                onClick={() => editor.chain().focus().toggleHeading({ level }).run()}
              >
                Heading {level}
              </MenuItem>
            ))}
          </MenuList>
        </Menu>

        <Divider orientation="vertical" h="20px" mx={1} />

        {/* Lists */}
        <ToolbarButton
          icon={<FiList />}
          label="Bullet List"
          isActive={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        />
        <ToolbarButton
          icon={<MdFormatListNumbered />}
          label="Numbered List"
          isActive={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        />
        <ToolbarButton
          icon={<MdFormatQuote />}
          label="Blockquote"
          isActive={editor.isActive('blockquote')}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        />

        <Divider orientation="vertical" h="20px" mx={1} />

        {/* Alignment (text blocks or a selected image) */}
        <ToolbarButton
          icon={<FiAlignLeft />}
          label={onImage ? 'Align Image Left' : 'Align Left'}
          isActive={alignActive('left')}
          onClick={() => applyAlign('left')}
        />
        <ToolbarButton
          icon={<FiAlignCenter />}
          label={onImage ? 'Center Image' : 'Align Center'}
          isActive={alignActive('center')}
          onClick={() => applyAlign('center')}
        />
        <ToolbarButton
          icon={<FiAlignRight />}
          label={onImage ? 'Align Image Right' : 'Align Right'}
          isActive={alignActive('right')}
          onClick={() => applyAlign('right')}
        />

        <Divider orientation="vertical" h="20px" mx={1} />

        {/* Link */}
        <Popover
          placement="bottom-start"
          initialFocusRef={urlInputRef}
          onOpen={handleLinkPopoverOpen}
          isLazy
        >
          {({ onClose: closeLinkPopover }) => (
            <>
              <PopoverTrigger>
                <IconButton
                  icon={<FiLink />}
                  aria-label="Insert Link"
                  size="xs"
                  variant={editor.isActive('link') ? 'solid' : 'ghost'}
                  colorScheme={editor.isActive('link') ? 'blue' : 'gray'}
                />
              </PopoverTrigger>
              <PopoverContent w="280px" bg={popoverBg} _focus={{ outline: 'none' }}>
                <PopoverBody p={3}>
                  <VStack spacing={2} align="stretch">
                    <Box>
                      <Text fontSize="xs" fontWeight="medium" mb={1}>URL</Text>
                      <Input
                        ref={urlInputRef}
                        size="sm"
                        value={linkUrl}
                        onChange={e => setLinkUrl(e.target.value)}
                        placeholder="https://..."
                        onKeyDown={e => e.key === 'Enter' && applyLink(closeLinkPopover)}
                      />
                    </Box>
                    {!hasContext && (
                      <Box>
                        <Text fontSize="xs" fontWeight="medium" mb={1}>Display Text</Text>
                        <Input
                          size="sm"
                          value={linkText}
                          onChange={e => setLinkText(e.target.value)}
                          placeholder="Link text (optional)"
                          onKeyDown={e => e.key === 'Enter' && applyLink(closeLinkPopover)}
                        />
                      </Box>
                    )}
                    <HStack spacing={2} justify="flex-end" pt={1}>
                      {editor.isActive('link') && (
                        <Button size="xs" variant="ghost" colorScheme="red" onClick={() => removeLink(closeLinkPopover)}>
                          Remove
                        </Button>
                      )}
                      <Button size="xs" variant="ghost" onClick={closeLinkPopover}>Cancel</Button>
                      <Button size="xs" colorScheme="blue" onClick={() => applyLink(closeLinkPopover)}>Apply</Button>
                    </HStack>
                  </VStack>
                </PopoverBody>
              </PopoverContent>
            </>
          )}
        </Popover>

        {/* Horizontal Rule */}
        <ToolbarButton
          icon={<FiMinus />}
          label="Horizontal Rule"
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
        />

        {/* Extra buttons slot (image upload, variable insert, etc.) */}
        {extraButtons}

        <Box flex={1} />

        {/* Undo / Redo */}
        <ToolbarButton
          icon={<MdUndo />}
          label="Undo (Ctrl+Z)"
          onClick={() => editor.chain().focus().undo().run()}
          isDisabled={!editor.can().undo()}
        />
        <ToolbarButton
          icon={<MdRedo />}
          label="Redo (Ctrl+Shift+Z)"
          onClick={() => editor.chain().focus().redo().run()}
          isDisabled={!editor.can().redo()}
        />
      </HStack>
    </Box>
  );
}
